import type {
  CoreConfiguration,
  DropinConfiguration,
  ICore,
} from "@adyen/adyen-web";
import { Card, Dropin, Klarna, PayPal, ApplePay } from "@adyen/adyen-web";
import { useMarket } from "@app/_context/marketContext";
import { getAdyenCheckoutForm } from "@server/requests/checkout/getAdyenCheckoutForm";
import { getStockWatch } from "@server/requests/checkout/stockWatch";
import getElevateSession from "@server/requests/elevate/getElevateSession";
import { getCookies } from "cookies-next";
import { useQuery } from "@tanstack/react-query";
import type { RefObject } from "react";
import { useEffect } from "react";

import { BRAND_CODE } from "@/config/common";
import { mapLocaleToAdyenLocale } from "@/config/market-configurations";
import { getCartCookie } from "@/utils/cart/getCartCookie";

import { useCheckout } from "../../Checkout.hooks";
import { cartToStockWatch } from "../../Checkout.utils";
import { adyenStore } from "./Adyen.store";
import { createAdyenCheckoutInstance, getAdyenReturnUrl } from "./Adyen.utils";
import { getAdyenCheckoutOrderForComplete } from "@server/requests/checkout/getAdyenCheckoutOrderForComplete";

/**
 * Adyen Dropin configuration used for the widget
 */
export function useAdyenDropInConfiguration() {
  const klarnaConfiguration = {
    useKlarnaWidget: true, // When set to true, the Klarna widget is shown. Set to false to initiate a redirect flow.
  };

  const configuration = {
    paymentMethodComponents: [Card, PayPal, Klarna, ApplePay],
    paymentMethodsConfiguration: {
      klarna: klarnaConfiguration,
      klarna_account: klarnaConfiguration,
      klarna_paynow: klarnaConfiguration,
    },
    onReady: () => {
      console.info("useAdyenDropInConfiguration - initialized and ready");
    },
    onSelect: (paymentMethod) => {
      console.info(
        "useAdyenDropInConfiguration - changed payment method",
        paymentMethod,
      );
    },
    beforeRedirect: (details) => {
      console.info("useAdyenDropInConfiguration - before redirect", details);
    },
  } satisfies DropinConfiguration;

  return configuration;
}

/**
 *  Get Adyen checkout form
 */
export function useAdyenCheckoutForm() {
  const cartId = getCartCookie()?.value ?? "";
  const { adyenMarket, contentfulLocale, locale, elevateMarket } =
    useMarket().state.market;
  const { data, refetch } = useCheckout();
  const returnURL = getAdyenReturnUrl({ locale, cartId });
  const elevateSession = getElevateSession();
  const cookies = Object.fromEntries(
    Object.entries(getCookies() ?? {}).filter((entry): entry is [string, string] => entry[1] !== undefined),
  );

  return useQuery({
    queryKey: ["adyenCheckoutForm"],
    queryFn: () =>
      getStockWatch(cartToStockWatch(data)).then(async (stockWatch) => {
        if (stockWatch.length > 0) {
          await refetch();
          window.scrollTo({ top: 0, behavior: "smooth" });
          throw "";
        }

        return getAdyenCheckoutForm({
          cartId: cartId,
          brandCode: BRAND_CODE,
          marketCode: adyenMarket,
          localeCode: mapLocaleToAdyenLocale(contentfulLocale),
          returnURL,
          elevateSessionKey: elevateSession.sessionKey,
          elevateCustomerKey: elevateSession.customerKey,
          elevateMarket: elevateMarket,
          cookies,
        });
      }),
    // We will fetch the adyen checkout session data manually in the component using the useRefetchCheckoutForm hook.
    // By this we can control when to refetch the session for example when the cart total changes.
    enabled: false,
  });
}

/**
 * Get Adyen checkout instance
 */

export function useAdyenCheckoutInstance(
  {
    id,
    sessionData,
  }: {
    id: string;
    sessionData?: string;
  },
  adyenConfiguration?: Partial<CoreConfiguration>,
) {
  const { contentfulLocale, adyenMarket } = useMarket().state.market;

  return useQuery({
    queryKey: ["adyenCheckoutInstance", id],
    queryFn: () =>
      createAdyenCheckoutInstance(
        {
          id,
          sessionData,
          locale: contentfulLocale,
          countryCode: adyenMarket,
        },
        adyenConfiguration,
      ),
    enabled: Boolean(id),
  });
}

/**
 * Mount Adyen drop-in widget
 */
export function useAdyenWidgetMount({
  data,
  ref,
}: {
  data?: ICore;
  ref?: RefObject<HTMLDivElement | null>;
}) {
  const adyenDropinConfiguration = useAdyenDropInConfiguration();
  const setDropIn = adyenStore((state) => state.setDropin);

  useEffect(() => {
    if (!ref?.current) {
      console.log("AdyenWidgetMount ref is not defined");
      return;
    }

    if (!data) {
      console.error("AdyenWidgetMount data is not defined");
      return;
    }

    const dropin = new Dropin(data, adyenDropinConfiguration);

    setDropIn(dropin);

    dropin.mount(ref.current);

    return () => {
      dropin.unmount();
      setDropIn(undefined);
    };
  }, [ref, data, adyenDropinConfiguration, setDropIn]);
}

/**
 * Get the Adyen order on the checkout confirmation page
 */
export function useAdyenCheckoutOrder({
  orderRef,
  sessionId,
  sessionResult,
  marketCode,
}: {
  orderRef: string;
  sessionId: string;
  sessionResult?: string;
  redirectResult?: string;
  marketCode: string;
}) {
  return useQuery({
    queryKey: ["getAdyenCheckoutOrderForComplete", orderRef],
    queryFn: () =>
      getAdyenCheckoutOrderForComplete({
        orderRef,
        sessionId,
        sessionResult,
        marketCode,
        brandCode: BRAND_CODE,
      }),
    enabled: Boolean(sessionResult),
    refetchOnWindowFocus: false,
  });
}
